import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, KeyRound, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Two-step identity capture for interactive galleries:
 *   Step 1: email + display_name → POST /collaborators/start (mints code)
 *   Step 2: 6-digit code         → POST /collaborators/verify (issues token)
 *
 * Editorial styling intentionally mirrors PremiumPasswordGate (full-bleed
 * cover backdrop, glass panel, accent button) so the password-then-identity
 * sequence feels like one continuous experience.
 */
export default function CollaboratorGate({ gallery, onVerified }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef = useRef(null);

  const bg = gallery.cover_image
    ? (gallery.cover_image.startsWith("http") ? gallery.cover_image : `${BACKEND}${gallery.cover_image}`)
    : null;
  const accent = gallery.accent_color || "#C9A961";

  useEffect(() => {
    if (step === 2) {
      // Focus the OTP input as soon as it mounts.
      setTimeout(() => codeInputRef.current?.focus(), 60);
    }
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  async function requestCode(e) {
    e?.preventDefault();
    if (!email.trim() || !displayName.trim()) return toast.error("Enter your name and email");
    setBusy(true);
    try {
      await axios.post(`${API}/galleries/${gallery.slug}/collaborators/start`, {
        email: email.trim().toLowerCase(),
        display_name: displayName.trim(),
      });
      toast.success("Code sent — check your inbox");
      setStep(2);
      setResendCooldown(60);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send code");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (resendCooldown > 0) return;
    setBusy(true);
    try {
      await axios.post(`${API}/galleries/${gallery.slug}/collaborators/start`, {
        email: email.trim().toLowerCase(),
        display_name: displayName.trim(),
      });
      toast.success("New code sent");
      setResendCooldown(60);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't resend code");
    } finally {
      setBusy(false);
    }
  }

  async function verify(e) {
    e?.preventDefault();
    if (code.trim().length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/galleries/${gallery.slug}/collaborators/verify`, {
        email: email.trim().toLowerCase(),
        code: code.trim(),
      });
      toast.success(`Welcome, ${data.display_name}`);
      onVerified(data.token);
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Invalid code";
      toast.error(detail);
      if (status === 429 || status === 423) {
        // locked out → kick back to step 1 so the user can retry later
        setStep(1);
        setCode("");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="relative min-h-screen w-full text-white overflow-hidden bg-stone-950"
      data-testid="collaborator-gate"
    >
      {bg && (
        <motion.div
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.2, 0.65, 0.3, 0.9] }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bg})` }}
        />
      )}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="text-[10px] tracking-[0.32em] uppercase mb-4 opacity-80">
              {gallery.studio_name || "An invitation"}
            </div>
            <h1
              className="font-light leading-[0.95] tracking-tight text-4xl sm:text-5xl"
              style={{ fontFamily: "var(--gallery-font-serif, 'Cormorant Garamond', serif)" }}
            >
              {gallery.name}
            </h1>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1, delay: 0.55 }}
              style={{ backgroundColor: accent, transformOrigin: "center" }}
              className="h-px w-20 mx-auto mt-8"
            />
          </div>

          <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 sm:p-9">
            {step === 1 ? (
              <form onSubmit={requestCode} className="space-y-4" data-testid="collab-gate-step1">
                <div className="flex flex-col items-center text-center mb-2">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-3">
                    <Mail className="w-4 h-4" />
                  </div>
                  <p className="text-sm opacity-75 leading-relaxed">
                    Tell us who you are so the studio can attribute your favorites and notes.
                  </p>
                </div>
                <label className="block">
                  <span className="text-[10px] tracking-[0.28em] uppercase opacity-70">Your name</span>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    autoComplete="name"
                    className="mt-1 w-full bg-transparent border-b border-white/30 py-2 outline-none focus:border-white tracking-wide placeholder:text-white/30 transition-colors"
                    placeholder="e.g. Ada Lovelace"
                    data-testid="collab-gate-name"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] tracking-[0.28em] uppercase opacity-70">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="mt-1 w-full bg-transparent border-b border-white/30 py-2 outline-none focus:border-white tracking-wide placeholder:text-white/30 transition-colors"
                    placeholder="you@example.com"
                    data-testid="collab-gate-email"
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy || !email || !displayName}
                  style={{ backgroundColor: accent }}
                  className="w-full px-5 h-12 text-stone-900 text-xs uppercase tracking-[0.28em] font-medium hover:opacity-90 disabled:opacity-40 transition-opacity mt-2"
                  data-testid="collab-gate-send-code"
                >
                  {busy ? "Sending…" : "Email me a code"}
                </button>
              </form>
            ) : (
              <form onSubmit={verify} className="space-y-4" data-testid="collab-gate-step2">
                <div className="flex flex-col items-center text-center mb-2">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-3">
                    <KeyRound className="w-4 h-4" />
                  </div>
                  <p className="text-sm opacity-75 leading-relaxed">
                    We sent a 6-digit code to <span className="text-white">{email}</span>. Enter it below.
                  </p>
                </div>
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full bg-transparent border-b border-white/30 py-3 outline-none focus:border-white text-center placeholder:text-white/30 text-2xl tracking-[0.5em] transition-colors"
                  placeholder="000000"
                  data-testid="collab-gate-code"
                />
                <button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  style={{ backgroundColor: accent }}
                  className="w-full px-5 h-12 text-stone-900 text-xs uppercase tracking-[0.28em] font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
                  data-testid="collab-gate-verify"
                >
                  {busy ? "Verifying…" : "Enter gallery"}
                </button>
                <div className="flex items-center justify-between text-[11px] tracking-[0.18em] uppercase opacity-70 pt-2">
                  <button
                    type="button"
                    onClick={() => { setStep(1); setCode(""); }}
                    className="hover:opacity-100 transition-opacity"
                    data-testid="collab-gate-back"
                  >
                    ← Change email
                  </button>
                  <button
                    type="button"
                    onClick={resend}
                    disabled={resendCooldown > 0 || busy}
                    className="inline-flex items-center gap-1.5 hover:opacity-100 transition-opacity disabled:opacity-40"
                    data-testid="collab-gate-resend"
                  >
                    <RefreshCcw className="w-3 h-3" />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="text-center text-[10px] tracking-[0.22em] uppercase opacity-50 mt-6">
            Lumetier · {gallery.studio_name || "Private collaboration"}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
